import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  Paper,
  Stack,
} from "@mui/material"
import type { FC } from "react"
import { useTranslation } from "react-i18next"
import type { Placement, VisibleBySlot } from "appsShared/inAppMessages"
import { InAppMessageCard } from "./InAppMessageCard"
import { useInAppMessageClose } from "./useInAppMessageClose"

type Corner = Extract<Placement, { type: "toast" }>["corner"]

/** `top` = `useOverlayTop()`: below the app bar and the in-flow banners (F1.1). */
const cornerSx = (corner: Corner, top: number): object =>
  ({
    "top-left": { top, left: 16 },
    "top-right": { top, right: 16 },
    "bottom-left": { bottom: 16, left: 16 },
    "bottom-right": { bottom: 16, right: 16 },
  })[corner]

const cornerOf = (p: Placement): Corner =>
  p.type === "toast" ? p.corner : "bottom-right"

/** Fixed full-width bar at the bottom of the viewport. */
export const InAppBottomSlot: FC<{ visible: VisibleBySlot["bottom"] }> = ({
  visible,
}) => {
  const { dismissHandler } = useInAppMessageClose()
  if (visible.length === 0) return null
  return (
    <Box
      data-ui="UI-SL2W-284"
      sx={{
        position: "fixed",
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: (theme) => theme.zIndex.snackbar,
      }}
    >
      {visible.map(({ message }) => (
        <InAppMessageCard
          key={message.id}
          message={message}
          onClose={dismissHandler(message)}
          square
        />
      ))}
    </Box>
  )
}

/** Up to two toasts (engine cap), stacked per corner. */
export const InAppToastSlot: FC<{
  visible: VisibleBySlot["toast"]
  top: number
}> = ({ visible, top }) => {
  const { dismissHandler } = useInAppMessageClose()
  if (visible.length === 0) return null
  const corners = Array.from(new Set(visible.map((v) => cornerOf(v.placement))))
  return (
    <>
      {corners.map((corner) => (
        <Stack
          key={corner}
          data-ui="UI-SL2W-285"
          spacing={1}
          sx={{
            position: "fixed",
            ...cornerSx(corner, top),
            width: 380,
            maxWidth: "calc(100vw - 32px)",
            zIndex: (theme) => theme.zIndex.snackbar,
          }}
        >
          {visible
            .filter((v) => cornerOf(v.placement) === corner)
            .map(({ message }) => (
              <Paper key={message.id} elevation={6}>
                <InAppMessageCard
                  message={message}
                  onClose={dismissHandler(message)}
                />
              </Paper>
            ))}
        </Stack>
      ))}
    </>
  )
}

/**
 * One modal per page session (engine cap). It always closes — a modal with no exit would
 * lock the console — and the close stores a dismissal only when the policy allows one.
 */
export const InAppModalSlot: FC<{ visible: VisibleBySlot["modal"] }> = ({
  visible,
}) => {
  const { t } = useTranslation()
  const { closeHandler } = useInAppMessageClose()
  const message = visible[0]?.message
  if (!message) return null
  const onClose = closeHandler(message)
  return (
    <Dialog
      open
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      slotProps={{ paper: { "data-ui": "UI-SL2W-286" } as object }}
    >
      <DialogContent sx={{ p: 0 }}>
        <InAppMessageCard message={message} square />
      </DialogContent>
      <DialogActions>
        <Button variant="contained" onClick={onClose}>
          {t("inAppMessageAcknowledge")}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

/** A floating side panel on the right, below the app bar and the in-flow banners. */
export const InAppPanelSlot: FC<{
  visible: VisibleBySlot["panel"]
  top: number
}> = ({ visible, top }) => {
  const { dismissHandler } = useInAppMessageClose()
  const message = visible[0]?.message
  if (!message) return null
  return (
    <Paper
      data-ui="UI-SL2W-287"
      elevation={8}
      sx={{
        position: "fixed",
        top,
        right: 16,
        width: 360,
        maxWidth: "calc(100vw - 32px)",
        zIndex: (theme) => theme.zIndex.snackbar - 1,
      }}
    >
      <InAppMessageCard message={message} onClose={dismissHandler(message)} />
    </Paper>
  )
}
